<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/vue/24/outline';
import { initials, avatarHue } from '@/util/avatar';
import CaseIdsPopover from './CaseIdsPopover.vue';
import ProviderBadge from './ProviderBadge.vue';

const props = defineProps<{
  name: string;
  caseId: string;
  provider: 'frankieOne' | 'sumsub';
  providerCaseUrl: string;
  zendeskCaseUrl?: string;
  internalEscalationSummary: string;
  partnerEscalationSummary: string;
  partner: string;
  region: string;
  externalId: string;
  externalCheckId?: string;
  accountId?: string;
  createdAt: string;
  lastSavedAt?: string | null;
  lastReviewedBy?: string | null;
  completedAt?: string | null;
  supersedingAmlReviewId?: string;
  ready: boolean;
  submitted: boolean;
  // Whether there are edits not yet persisted. Surfaced on the Save button
  // itself — before this, `dirty` drove only the leave-confirmation dialog,
  // so the first time a reviewer heard about unsaved work was the moment
  // they tried to navigate away (see TASK-13728).
  dirty: boolean;
  saving: boolean;
  submitting: boolean;
  kbEnabled: boolean;
  paneVisible: boolean;
}>();

const emit = defineEmits<{
  back: [];
  save: [];
  submit: [];
  'toggle-kb': [];
  'open-shortcuts': [];
  'toggle-pane': [];
}>();

const hue = computed(() => avatarHue(props.name));

const summaryCopied = ref(false);
let summaryCopiedTimer: ReturnType<typeof setTimeout> | undefined;
async function copyEscalationSummary(text: string) {
  await navigator.clipboard.writeText(text);
  summaryCopied.value = true;
  clearTimeout(summaryCopiedTimer);
  summaryCopiedTimer = setTimeout(() => {
    summaryCopied.value = false;
  }, 1500);
}
onUnmounted(() => clearTimeout(summaryCopiedTimer));
</script>

<template>
  <div
    class="hidden min-h-14 flex-none flex-wrap items-center gap-x-3.5 gap-y-2 border-b border-gray-200 bg-white px-4 py-2 lg:flex"
    data-test="case-status-bar"
  >
    <button
      type="button"
      aria-label="Back to queue"
      data-test="btn-back-to-queue"
      class="flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50"
      @click="emit('back')"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 4 6 10l6 6" />
      </svg>
    </button>

    <div
      class="flex h-8 w-8 flex-none items-center justify-center rounded-md text-xs font-bold"
      :style="{ backgroundColor: `hsl(${hue} 90% 94%)`, color: `hsl(${hue} 60% 32%)` }"
    >
      {{ initials(name) }}
    </div>
    <div class="flex min-w-0 flex-1 flex-col">
      <span class="flex min-w-0 items-center gap-1.5">
        <span class="truncate text-sm font-semibold text-gray-900">{{ name }}</span>
        <span class="flex-none text-gray-300">/</span>
        <span class="truncate text-xs font-medium text-gray-600">{{ partner }}</span>
        <span
          class="flex-none rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-gray-600"
        >
          {{ region }}
        </span>
        <ProviderBadge :provider="provider" :href="providerCaseUrl" />
        <a
          v-if="zendeskCaseUrl"
          :href="zendeskCaseUrl"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open in Zendesk"
          title="Open in Zendesk"
          class="inline-flex flex-none items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          Zendesk
          <ArrowTopRightOnSquareIcon class="size-3.5" />
        </a>
      </span>
      <span class="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[11.5px] text-gray-500">
        <span class="truncate font-mono">{{ caseId }}</span>
        <CaseIdsPopover
          :case-id="caseId"
          :account-id="accountId"
          :external-id="externalId"
          :external-check-id="externalCheckId"
          :created-at="createdAt"
          :last-saved-at="lastSavedAt"
          :last-reviewed-by="lastReviewedBy"
          :completed-at="completedAt"
          :superseding-aml-review-id="supersedingAmlReviewId"
        />
      </span>
    </div>

    <div
      class="flex flex-none items-center gap-2 rounded-full border border-gray-300 bg-gray-50 py-1 pr-1 pl-2.5"
    >
      <span class="text-xs font-medium text-gray-600">Keyboard</span>
      <button
        type="button"
        role="switch"
        :aria-checked="kbEnabled"
        aria-label="Keyboard shortcuts"
        data-test="switch-kb"
        class="relative h-5 w-[34px] flex-none rounded-full transition-colors"
        :class="kbEnabled ? 'bg-indigo-600' : 'bg-gray-300'"
        @click="emit('toggle-kb')"
      >
        <span
          class="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[left]"
          :class="kbEnabled ? 'left-[16px]' : 'left-0.5'"
        ></span>
      </button>
    </div>

    <button
      type="button"
      data-test="btn-open-shortcuts"
      class="flex h-8 flex-none items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
      @click="emit('open-shortcuts')"
    >
      Shortcuts
      <span class="rounded border border-gray-300 px-1 font-mono text-[10px] text-gray-400">?</span>
    </button>

    <button
      type="button"
      data-test="btn-toggle-pane"
      class="h-8 flex-none rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-600 hover:bg-gray-50"
      @click="emit('toggle-pane')"
    >
      {{ paneVisible ? 'Hide pane' : 'Show pane' }}
    </button>

    <Menu as="div" class="relative flex-none">
      <MenuButton
        data-test="btn-copy-zendesk-summary"
        :aria-label="summaryCopied ? 'Copied' : 'Copy for Zendesk'"
        :title="summaryCopied ? 'Copied' : 'Copy for Zendesk'"
        class="flex h-8 flex-none items-center gap-1 rounded-md border border-gray-300 bg-white px-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
      >
        <ClipboardDocumentCheckIcon v-if="summaryCopied" class="size-3.5 text-green-600" />
        <ClipboardDocumentIcon v-else class="size-3.5" />
        Zendesk
      </MenuButton>
      <MenuItems
        class="absolute right-0 z-20 mt-1 w-56 rounded-md border border-gray-200 bg-white py-1 text-xs shadow-lg focus:outline-none"
      >
        <MenuItem v-slot="{ active }">
          <button
            type="button"
            data-test="menu-item-internal-escalation"
            class="block w-full px-3 py-1.5 text-left text-gray-700"
            :class="active && 'bg-gray-50'"
            @click="copyEscalationSummary(internalEscalationSummary)"
          >
            Internal escalation
          </button>
        </MenuItem>
        <MenuItem v-slot="{ active }">
          <button
            type="button"
            data-test="menu-item-partner-escalation"
            class="block w-full px-3 py-1.5 text-left text-gray-700"
            :class="active && 'bg-gray-50'"
            @click="copyEscalationSummary(partnerEscalationSummary)"
          >
            Partner escalation
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>

    <button
      type="button"
      data-test="btn-save"
      :disabled="saving || submitted"
      :title="dirty ? 'Unsaved changes' : 'No changes to save'"
      class="flex h-8 flex-none items-center gap-1 rounded-md border px-2.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
      :class="
        dirty && !saving
          ? 'border-[var(--color-indigo-tint-border)] bg-[var(--color-indigo-tint-bg)] text-[var(--color-indigo-tint-text)]'
          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
      "
      @click="emit('save')"
    >
      <span
        v-if="dirty && !saving"
        data-test="unsaved-dot"
        class="size-1.5 flex-none rounded-full bg-current"
        aria-hidden="true"
      ></span>
      {{ saving ? 'Saving…' : 'Save' }}
      <span v-if="!saving" class="font-mono opacity-60">⌘S</span>
    </button>
    <button
      type="button"
      data-test="btn-submit"
      :disabled="!ready || submitted || submitting"
      class="flex h-8 flex-none items-center gap-1 rounded-md px-3 text-xs font-semibold"
      :class="
        ready && !submitted && !submitting
          ? 'cursor-pointer bg-indigo-600 text-white hover:bg-indigo-700'
          : 'cursor-not-allowed bg-gray-100 text-gray-400'
      "
      @click="emit('submit')"
    >
      {{ submitted ? 'Submitted' : submitting ? 'Submitting…' : 'Submit' }}
      <span v-if="!submitted && !submitting" class="font-mono opacity-70">⌘⏎</span>
    </button>
  </div>
</template>
